import React, { useEffect, useMemo, useRef } from 'react';
import LocalOpsSheet from './LocalOpsSheet';

const formatPrice = (price = {}) =>
  price.display ||
  (price.amountCents ? `$${(price.amountCents / 100).toFixed(2)}` : '$9.99');

const formatApproxCurrency = (amount) => {
  const number = Number(amount);
  if (!Number.isFinite(number) || number <= 0) return '';
  return `$${Math.round(number).toLocaleString('en-US')}`;
};

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export default function LocalOpsFoundingHelperPassModal({
  open,
  offer,
  busy = false,
  error = '',
  onPrimary,
  onSkip,
  onDismiss,
}) {
  const primaryRef = useRef(null);
  const region = offer?.regionDisplayName || 'Local';
  const offerName = offer?.offerName || `${region} Founding Helper Pass`;
  const priceLabel = useMemo(() => formatPrice(offer?.price), [offer?.price]);
  const benefits = offer?.benefits || {};
  const firstLookMinutes = toPositiveNumber(
    benefits.firstLookTasks?.windowMinutes,
  );
  const feeTier = benefits.helperFeeTier || {};
  const standardFeePercent = toPositiveNumber(feeTier.standardFeePercent);
  const founderFeePercent = toPositiveNumber(feeTier.founderFeePercent);
  const computedDiscount =
    standardFeePercent && founderFeePercent
      ? standardFeePercent - founderFeePercent
      : null;
  const discountPercent =
    toPositiveNumber(feeTier.discountPercent) ||
    (computedDiscount && computedDiscount > 0 ? computedDiscount : null);
  const priceAmountCents = toPositiveNumber(offer?.price?.amountCents);
  const breakEvenEarningsLabel =
    priceAmountCents && discountPercent
      ? formatApproxCurrency(priceAmountCents / discountPercent)
      : '';
  const firstLookTitle = firstLookMinutes
    ? `${firstLookMinutes}-minute head start`
    : 'Early task access';
  const founderFeeTitle =
    standardFeePercent && founderFeePercent
      ? `${founderFeePercent}% platform fee instead of ${standardFeePercent}%`
      : founderFeePercent
      ? `${founderFeePercent}% platform fee`
      : 'Lower platform fee';
  const mathHasNumbers = standardFeePercent && founderFeePercent;

  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      primaryRef.current?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  return (
    <LocalOpsSheet
      open={open}
      onClose={onDismiss}
      title={offerName}
      ariaLabel={offerName}
      className="localops-sheet--centered localops-founding-pass"
    >
      <div className="localops-founding-pass__body">
        <div className="localops-founding-pass__eyebrow">
          {region.toUpperCase()} LAUNCH / FOUNDING RATE
        </div>
        <p className="localops-founding-pass__hero">
          Get a head start. Keep more of what you earn.
        </p>
        <p className="localops-founding-pass__intro">
          One-time payment. No monthly fee or subscription. Get early task
          access and lower platform fees in {region}.
        </p>

        <div className="localops-founding-pass__benefits">
          <div className="localops-founding-pass__benefit">
            <strong>{firstLookTitle}</strong>
            <span>
              Get early access to accept eligible paid tasks in {region}.
            </span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>Founding Helper status</strong>
            <span>Show your Founding Helper status on your JG Player profile.</span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>{founderFeeTitle}</strong>
            <span>
              Keep more of your earnings with a lower platform fee on eligible
              completed tasks.
            </span>
          </div>
          <div className="localops-founding-pass__benefit">
            <strong>Priority when helper setup is waitlisted</strong>
            <span>
              Get priority access to helper setup in {region} when a waitlist
              applies.
            </span>
          </div>
        </div>

        {mathHasNumbers ? (
          <details className="localops-founding-pass__math">
            <summary>How the math works</summary>
            <div>
              <p>Standard helper task fee: {standardFeePercent}%</p>
              <p>Founding Helper task fee: {founderFeePercent}%</p>
              {discountPercent ? (
                <p>
                  The platform fee is {discountPercent} percentage points lower
                  on eligible completed task earnings.
                </p>
              ) : null}
              {breakEvenEarningsLabel ? (
                <p>
                  At that difference, the {priceLabel} pass can pay for itself
                  after about {breakEvenEarningsLabel} in eligible completed
                  task earnings.
                </p>
              ) : null}
              <p>
                No guaranteed tasks or earnings. Safety, location, trust, helper
                setup, and task requirements still apply.
              </p>
            </div>
          </details>
        ) : null}

        <div className="localops-founding-pass__price">
          <strong>{priceLabel}, one time.</strong>
          <span>No monthly fee. No subscription.</span>
        </div>

        <p className="localops-founding-pass__fomo">
          This is a launch-window offer. The founding rate, benefits, and
          availability may change as {region} fills in.
        </p>
        <p className="localops-founding-pass__disclaimer">
          No guaranteed tasks or earnings. Safety, location, trust, helper setup,
          and task requirements still apply.
        </p>
      </div>

      {error ? (
        <div className="localops-founding-pass__error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="localops-founding-pass__actions">
        <button
          type="button"
          className="localops-btn localops-founding-pass__primary"
          onClick={onPrimary}
          disabled={busy}
          ref={primaryRef}
        >
          {busy
            ? 'Opening Stripe...'
            : `Get my pass — ${priceLabel} one time`}
        </button>
        <button
          type="button"
          className="localops-btn localops-btn--ghost localops-founding-pass__secondary"
          onClick={onSkip}
          disabled={busy}
        >
          Continue without pass
        </button>
      </div>
    </LocalOpsSheet>
  );
}
